import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { adjustTiersValues, isAdding, isSameDimension, syncTiers } from "@/PayPlans/helpers/Tiers";

// Hooks
import { detachValidate, isValidMapErrors } from "@/Common/hooks/Validations";

// Contexts
import { usePayPlan } from "@/PayPlans/context/PayPlans";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import TieredProduct from "@/Layouts/components/TieredProduct/TieredProduct";

// MUI
import { Grid } from "@mui/material";

const NewAndPreOwnedRequiredLabel = (
	<span>
		New <strong style={{ textDecoration: "underline" }}>and</strong> Pre-Owned Required
	</span>
);

const validateBeforeSend = (tiers = []) => {
	const tier = tiers?.[0] || null;
	const hasProducts = Boolean(tier?.frontProducts?.length);
	const frontDistinct = hasProducts ? {} : { distinct: 0 };

	const validation = detachValidate([
		{
			definition: {
				useKey: "units",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Unit: 0.5 }, // Add delta on upper value
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers,
		},
		{
			definition: {
				useKey: "commissions",
				operators: {
					conditions: [
						{
							type: "SELF",
							on: "value",
							...frontDistinct,
						},
						{
							type: "STEP",
							on: "value",
							compare: "<=", // If current (upper) < next is good
							deltas: { Dollar: 0, Percent: 0 }, // Add delta on upper value
						},
					],
				},
				errorsMap: ["frontGross"],
				message: "Invalid front commission",
				validationType: "SIMPLE",
			},
			section: tiers,
		},
		{
			definition: {
				useKey: "backGross",
				operators: {
					conditions: [
						{
							type: "SELF",
							on: "value",
						},
						{
							type: "STEP",
							on: "value",
							compare: "<=", // If current (upper) <= next is good
							deltas: { Dollar: 0, Percent: 0 }, // Add delta on upper value
						},
					],
				},
				errorsMap: ["backGross"],
				message: "Invalid back commissions",
				validationType: "SIMPLE",
			},
			section: tiers,
		},
	]);
	return isValidMapErrors(validation);
};

const isAllValid = (errors = []) => !errors.length || [...errors].every((error) => error);

const SYNC_MODIFIERS = [
	{
		"from.value": {
			min: 0,
			max: 0,
			useLimitReferenceOn: "from.input",
			conditions: [
				{ isAdding: true, limit: "min" },
				{ isAdding: false, fromPrevious: true, value: "to.value", delta: 0.5 },
			],
		},
	},
	{
		"to.value": {
			min: 0,
			max: 100000,
			useLimitReferenceOn: "to.input",
			conditions: [{ isAdding: true, onBefore: true, value: 0 }],
		},
	},
	{ "frontGross.value": { min: 0, max: 0, notReduce: true } },
	{ "backGross.value": { min: 0, max: 0, notReduce: true } },
];

export default function DefaultCommissions(props) {
	const {
		sectionId = "default",
		commissions = null,
		frontProducts = [],
		backProducts = [],
		tabsProps = {
			tabs: ["New Vehicles", "Pre-Owned Vehicles"],
			errors: [],
			newAndPreOwnedRequired: false,
			globalUnitCounts: false,
			applyTiersRetroactively: false,
			isGuaranteedDraw: false,
		},
		loading = false,
		loadingProducts = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { useSectionsErrors } = usePayPlan();
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors();

	// States
	const [model, setModel] = useState(null);

	// Error handler
	const errors = useMemo(() => {
		const errorNew = tabsProps.errors?.includes(getErrorKey(sectionId, "new"));
		const errorPreOwned = tabsProps.errors?.includes(getErrorKey(sectionId, "preOwned"));
		return [errorNew, errorPreOwned];
	}, [sectionId, tabsProps.errors, getErrorKey]);

	// Actions handlers
	const updateAll = (section, data) => {
		setModel({ ...data });

		const storedErrors = Object.values(data?.commissions)?.map((tiers) => validateBeforeSend(tiers));

		onActionUpdate?.(
			section,
			"all",
			{
				content: data["commissions"],
				newAndPreOwnedRequired: data["tabsProps"]["newAndPreOwnedRequired"],
				globalUnitCounts: data["tabsProps"]["globalUnitCounts"],
				applyTiersRetroactively: data["tabsProps"]["applyTiersRetroactively"],
				isGuaranteedDraw: Boolean(data?.["tabsProps"]?.["isGuaranteedDraw"]),
			},
			isAllValid(storedErrors)
		);
	};

	const handleUpdateSwitches = (section, area, value) => {
		const updatedTabsProps = { ...model?.tabsProps, [area]: value };
		const update = { ...model, tabsProps: updatedTabsProps };

		if (area === "newAndPreOwnedRequired" && value) {
			update["tabsProps"]["globalUnitCounts"] = false;

			// Use to know if the reduction should be applied
			const needsToSynchronize = isSameDimension(model?.commissions);
			// Synchronize tiers
			let updatedCommissions = syncTiers({ ...update.commissions }, needsToSynchronize);
			// Adjust values
			updatedCommissions = adjustTiersValues(updatedCommissions, needsToSynchronize, {
				key: null,
				values: SYNC_MODIFIERS,
			});

			update["commissions"] = clone(updatedCommissions);
		}

		updateAll(section, update);
	};

	const handleUpdateTiers = (section, area, data) => {
		const previousData = model?.commissions?.[area];
		let updatedCommissions = { ...model?.commissions, [area]: data };

		if (model?.tabsProps?.newAndPreOwnedRequired) {
			// Use to know if the reduction should be applied
			const isAddingTiers = isAdding(data, previousData);
			// Synchronize tiers
			updatedCommissions = syncTiers({ ...updatedCommissions }, isAddingTiers);
			// Adjust values
			updatedCommissions = adjustTiersValues(updatedCommissions, isAddingTiers, {
				key: null,
				values: SYNC_MODIFIERS,
			});
		}
		const update = { ...model, commissions: clone(updatedCommissions) };
		updateAll(section, update);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	useEffect(() => {
		setModel({
			tabsProps,
			commissions,
		});

		return () => {
			setModel(null);
		};
	}, [tabsProps, commissions]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					!!model?.commissions && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={errors}
							content={Object.entries(model?.commissions)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<TieredProduct
											tiers={value || []}
											frontProducts={frontProducts || []}
											backProducts={backProducts || []}
											readOnly={readOnly}
											loadingProducts={loadingProducts}
											onActionUpdate={(updatedModel) => handleUpdateTiers(sectionId, key, updatedModel)}
											onActionValidation={(updatedValidation) => handleValidation(sectionId, key, updatedValidation)}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={"isGuaranteedDraw"}
									textLeft={true}
									textChecked={"Guaranteed Draw"}
									textUnChecked={"Guaranteed Draw"}
									checked={Boolean(model?.tabsProps?.isGuaranteedDraw)}
									disabled={readOnly || loading}
									onChange={(value) => handleUpdateSwitches(sectionId, "isGuaranteedDraw", value)}
									tooltip={{
										title: "Guarantee the draw.",
										placement: "left",
										key: "default-commissions-guarantee-draw-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"newAndPreOwnedRequired"}
									textLeft={true}
									textChecked={NewAndPreOwnedRequiredLabel}
									textUnChecked={NewAndPreOwnedRequiredLabel}
									checked={model?.tabsProps.newAndPreOwnedRequired}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "newAndPreOwnedRequired", value)}
									tooltip={{
										title: "Makes both tabs share the same number of rows and columns.",
										placement: "bottom",
										key: "default-commissions-new-and-preOwned-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"globalUnitCounts"}
									textLeft={true}
									textChecked={"Global unit counts"}
									textUnChecked={"Global unit counts"}
									checked={model?.tabsProps.globalUnitCounts}
									disabled={readOnly || model?.tabsProps.newAndPreOwnedRequired}
									onChange={(value) => handleUpdateSwitches(sectionId, "globalUnitCounts", value)}
									tooltip={{
										title: "Uses all units of New and Pre-Owned Vehicles together.",
										placement: "bottom",
										key: "default-commissions-global-unit-counts-tooltip",
									}}
								/>,
							]}
							bottomActions={[
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={model?.tabsProps.applyTiersRetroactively}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "applyTiersRetroactively", value)}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
DefaultCommissions.propTypes = {
	sectionId: PropTypes.string,
	commissions: PropTypes.object,
	frontProducts: PropTypes.array,
	backProducts: PropTypes.array,
	tabsProps: PropTypes.shape({
		tabs: PropTypes.array,
		errors: PropTypes.array,
		newAndPreOwnedRequired: PropTypes.bool,
		globalUnitCounts: PropTypes.bool,
		applyTiersRetroactively: PropTypes.bool,
		isGuaranteedDraw: PropTypes.bool,
	}),
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
